import { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router";
import { Button, Card, Switch, Typography } from "antd";
import { MinusOutlined, UndoOutlined } from "@ant-design/icons";
import CalendarIcon from "../../../assets/icons/calendar_today.svg?react";
import AlarmIcon from "../../../assets/icons/alarm.svg?react";
import {
  createSelectOptions,
  createSelectOptionsWithOrder,
  getGroupArrayOrdered,
  getUnsortedList,
} from "../../../utils/tableConfigs/ActivitiesTableUtilFunctions";
import FetchWrapper from "../../../components/UI/FetchWraper";

import InfoTooltip from "../../../components/UI/InfoTooltip";
import GuideButton from "../../../components/UI/GuideButton";
import TableDowloadButton from "../../../components/UI/DownloadTable";
import type { ComponentType } from "react";
import CheckboxSelectRaw from "../../../components/UI/CheckboxSelect";

const CheckboxSelect = CheckboxSelectRaw as ComponentType<any>;
import TooltipTag from "../../../components/UI/TooltipTag";
import {
  iconTypeMap,
  textColor,
  pillColor,
  tooltipTitle,
} from "../../../utils/tableConfigs/VisitsMapping";
import { isUnscheduledVisit } from "../../../utils/unscheduledVisitUtils/unscheduledVisitUtils";
const { Text } = Typography;

interface Activity {
  activity: string;
  activity_status: string;
  activity_type: string;
  timepoint: string;
  date: string;
  time: string;
  notes: string;
  [key: string]: unknown;
}

type CategoryObject = Record<string, any[]>;

interface SelectOption {
  label: string;
  value: string;
}

type GroupedActivities = Record<string, any[]>;

function filterDataByActivityStatus(data: CategoryObject[]): CategoryObject[] {
  return data
    .map((category) => {
      const key = Object.keys(category)[0];
      const activities = category[key];

      // Filter out activities with "Not Required" status
      const filteredActivities = activities.filter(
        (activity) => activity.activity_status !== "Not Required",
      );

      // Return a new object with the filtered activities
      return { [key]: filteredActivities };
      // return { [key]: activities };
    })
    .filter((category) => {
      const key = Object.keys(category)[0];
      return category[key].length > 0;
    });
}

function transformData(data: CategoryObject[]): Activity[] {
  return data.flatMap((obj) => {
    const key = Object.keys(obj)[0];
    return obj[key];
  });
}

function filterOutNonRequiredData(data: Activity[]): Activity[] {
  const allTimepoints = getUnsortedList(data, "timepoint");

  const allDataByCategory = getGroupArrayOrdered(
    data,
    allTimepoints,
    "timepoint",
    "activity",
  );
  const filteredDataByCategory = filterDataByActivityStatus(allDataByCategory);
  const cleanedData = transformData(filteredDataByCategory);
  return cleanedData;
}

const renderTags = (value: string) => {
  if (
    value === "Data Missing" ||
    value === "Data Pending" ||
    value === "Data Discrepancy"
  ) {
    return (
      <TooltipTag
        textColor={textColor[value]}
        tagColor={pillColor[value]}
        tooltipTitle={tooltipTitle[value]}
      >
        {value}
      </TooltipTag>
    );
  }
  if (value === "-") {
    return <MinusOutlined style={{ color: "#556C75" }} />;
  }
  return <span style={{ color: "#556C75" }}>{value}</span>;
};

const renderActivityStatusTag = (status: string) => {
  if (
    status === "Complete" ||
    status === "Not Done" ||
    status === "Not Required"
  ) {
    return (
      <TooltipTag //square tag
        textColor={textColor[status]}
        tagColor={pillColor[status]}
        tooltipTitle={tooltipTitle[status]}
        icon={iconTypeMap[status]}
        tagStyle={{ borderRadius: "5px" }}
      >
        {status}
      </TooltipTag>
    );
  }
  if (
    status === "Data Missing" ||
    status === "Data Pending" ||
    status === "Data Discrepancy"
  ) {
    return (
      <TooltipTag //round tag
        textColor={textColor[status]}
        tagColor={pillColor[status]}
        tooltipTitle={tooltipTitle[status]}
      >
        {status}
      </TooltipTag>
    );
  }
  return null;
};

export default function SingleSubjectSchedule() {
  const { subjectId } = useParams();
  return (
    <FetchWrapper
      endpointSuffix={`activities/${subjectId}/`}
      extraQueryKey={subjectId}
    >
      {(data: Activity[]) => (
        <FetchWrapper endpointSuffix={`activities/`}>
          {(activitiesData: GroupedActivities[]) => (
            <SingleSubjectScheduleComponent
              data={data}
              activitiesData={activitiesData}
            />
          )}
        </FetchWrapper>
      )}
    </FetchWrapper>
  );
}

interface SingleSubjectScheduleComponentProps {
  data: Activity[];
  activitiesData: GroupedActivities[];
}

function SingleSubjectScheduleComponent({
  data,
  activitiesData,
}: SingleSubjectScheduleComponentProps) {
  const subjectActivitiesData = useMemo(
    () =>
      activitiesData?.reduce<CategoryObject[]>((acc, activity) => {
        const thisActivityGroupName = Object.keys(activity)[0];
        const activitiesInThisGroup = activity[thisActivityGroupName];
        const thisActivityGroup = activitiesInThisGroup.map((a) => ({
          label: a,
          value: a,
        }));
        return [...acc, { [thisActivityGroupName]: thisActivityGroup }];
      }, []),
    [activitiesData],
  );

  const allActivities = useMemo<any[]>(
    () =>
      activitiesData?.reduce<any[]>((acc, activity) => {
        const thisActivityGroupName = Object.keys(activity)[0];
        const activitiesInThisGroup = activity[thisActivityGroupName];
        return [...acc, ...activitiesInThisGroup];
      }, []),
    [activitiesData],
  );

  const { subjectId } = useParams();

  const allData = filterOutNonRequiredData(data);
  const allTimepoints = getUnsortedList(allData, "timepoint");

  const scheduledOnlyTimepoints = allTimepoints.filter(
    (item: any) => !isUnscheduledVisit(item),
  );

  const unscheduledOnlyTimepoints = allTimepoints.filter((item: any) =>
    isUnscheduledVisit(item),
  );

  const allDataByCategory = getGroupArrayOrdered(
    allData,
    allTimepoints,
    "timepoint",
    "activity",
  );

  const scheduledOnlyData = allData.filter(
    (item) => !isUnscheduledVisit(item.timepoint),
  );
  const scheduledOnlyDataByCategory = getGroupArrayOrdered(
    scheduledOnlyData,
    scheduledOnlyTimepoints,
    "timepoint",
    "activity",
  );

  const _filteredData = scheduledOnlyDataByCategory.filter(
    (item: CategoryObject) =>
      item[Object.keys(item)[0]] && item[Object.keys(item)[0]].length > 0,
  );

  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [filteredData, setFilteredData] =
    useState<CategoryObject[]>(_filteredData);

  const timepointOptions: any[] = useMemo(
    () =>
      createSelectOptionsWithOrder(
        allData,
        "timepoint",
        showUnscheduled ? allTimepoints : scheduledOnlyTimepoints,
      ),
    [allData, showUnscheduled, allTimepoints, scheduledOnlyTimepoints],
  );

  const completionOptions: any[] = useMemo(
    () => createSelectOptions(allData, "activity_status"),
    [allData],
  );

  const [selectedStatus, setSelectedStatus] = useState<any[]>(
    completionOptions?.map((item: SelectOption) => item.value) || [],
  );

  const [selectedActivityType, setSelectedActivityType] =
    useState<any[]>(allActivities);

  const [selectedTimepoints, setSelectedTimepoints] = useState<any[]>(
    timepointOptions.map((item: SelectOption) => item.value) || [],
  );

  const filterData = useCallback(
    ({
      status,
      activityType,
      timepoints,
      showUnscheduledChecked,
    }: {
      status?: string[];
      activityType?: string[];
      timepoints?: string[];
      showUnscheduledChecked?: boolean;
    }) => {
      if (status) {
        setSelectedStatus(status);
      }
      if (activityType) {
        setSelectedActivityType(activityType);
      }
      if (timepoints) {
        setSelectedTimepoints(timepoints);
      }

      const _status = status || selectedStatus;

      const _activityType = activityType || selectedActivityType;
      const _timepoints = timepoints || selectedTimepoints;
      const _showUnscheduled = showUnscheduledChecked || showUnscheduled;

      const dataToFilter = _showUnscheduled
        ? allData
        : allData.filter((item) => !isUnscheduledVisit(item.timepoint));
      const timepointsToUse = _showUnscheduled
        ? allTimepoints
        : scheduledOnlyTimepoints;

      if (
        _activityType.length > 0 ||
        _timepoints.length > 0 ||
        _status.length > 0
      ) {
        const filtered = dataToFilter.filter(
          (item) =>
            _status.includes(item.activity_status) &&
            _activityType.includes(item.activity) &&
            _timepoints.includes(item.timepoint),
        );
        const datum = getGroupArrayOrdered(
          filtered,
          timepointsToUse,
          "timepoint",
          "activity",
        );

        const _filteredData = datum.filter(
          (item: CategoryObject) =>
            item[Object.keys(item)[0]] && item[Object.keys(item)[0]].length > 0,
        );

        setFilteredData(_filteredData);
      } else {
        setFilteredData(
          _showUnscheduled ? allDataByCategory : scheduledOnlyDataByCategory,
        );
      }
    },
    [
      showUnscheduled,
      allData,
      allDataByCategory,
      scheduledOnlyDataByCategory,
      allTimepoints,
      scheduledOnlyTimepoints,
      selectedStatus,
      selectedActivityType,
      selectedTimepoints,
    ],
  );

  const clearSelections = () => {
    filterData({
      status: completionOptions?.map((item) => item.value) || [],
      activityType: allActivities,
      timepoints: timepointOptions.map((item) => item.value) || [],
    });
  };

  const onChangeColumnSwitch = (checked: boolean) => {
    setShowUnscheduled(checked);
    if (checked) {
      filterData({
        timepoints: [...selectedTimepoints, ...unscheduledOnlyTimepoints],
        showUnscheduledChecked: checked,
      });
    } else {
      filterData({
        timepoints: selectedTimepoints.filter(
          (item) => !unscheduledOnlyTimepoints.includes(item),
        ),
        showUnscheduledChecked: checked,
      });
    }
  };

  const timepointHeaders = useMemo(() => {
    return filteredData.map((item) => Object.keys(item)[0]);
  }, [filteredData]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h1
            className="text-[15px] font-semibold"
            data-testid="schedule-page-subtitle"
          >
            Schedule of Activities
          </h1>
          <InfoTooltip
            title="Explore the selected subject's activities by week."
            data-testid="schedule-subtitle-tooltip"
          />
        </div>
        <div className="flex gap-[10px] items-center">
          <TableDowloadButton
            data={allData}
            name={`individual_subject_profile_schedule_activities_${subjectId}`}
          />
          <GuideButton pageName="/wiki/guides/exploration-singlesubject-schedule" />
        </div>
      </div>
      <div className="flex mb-5 items-center justify-between flex-wrap gap-4">
        <div className="flex gap-4 flex-wrap">
          <CheckboxSelect
            options={subjectActivitiesData || []}
            selectedItems={selectedActivityType}
            setSelectedItems={setSelectedActivityType}
            title="Select Activity"
            dataTestId="activity-select"
            config={{
              labels: { optionsName: "activities" },
              buttonStyle: { width: 200 },
              applyCallback: (activityType: string[]) =>
                filterData({ activityType }),
            }}
          />
          <CheckboxSelect
            options={timepointOptions}
            selectedItems={selectedTimepoints}
            setSelectedItems={setSelectedTimepoints}
            title="Select Timepoint"
            dataTestId="timepoint-select"
            config={{
              labels: { optionsName: "timepoints" },
              buttonStyle: { width: 200 },
              applyCallback: (timepoints: string[]) =>
                filterData({ timepoints }),
            }}
          />
          <CheckboxSelect
            options={completionOptions}
            selectedItems={selectedStatus}
            setSelectedItems={setSelectedStatus}
            title="Select Completion"
            dataTestId="completion-select"
            config={{
              labels: { optionsName: "completions" },
              buttonStyle: { width: 200 },
              applyCallback: (status: string[]) => filterData({ status }),
            }}
          />

          <Button
            type="text"
            className="p-0"
            icon={<UndoOutlined />}
            onClick={clearSelections}
          >
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <p className={showUnscheduled ? "text-[black]" : "text-[#556C75]"}>
            Show Unscheduled Visits
          </p>
          <Switch checked={showUnscheduled} onChange={onChangeColumnSwitch} />
        </div>
      </div>
      <div className="w-full max-w-full   overflow-y-auto max-h-[500px]">
        <div className="grid grid-flow-col auto-cols-max sticky top-0 z-10">
          {timepointHeaders.map((timepoint, index) => {
            return (
              <div key={index} data-testid={`timepoint-header-${index}`}>
                <div
                  key={index}
                  className={`${
                    isUnscheduledVisit(timepoint)
                      ? "bg-[#D9D9D9] text-[#094B68]"
                      : "bg-[#064C68] text-white"
                  }  flex w-65 h-9 items-center justify-center rounded-sm mb-4 mr-3 px-2`}
                >
                  <Text
                    ellipsis={{ tooltip: timepoint }}
                    style={{
                      textAlign: "center",
                      width: "100%",
                      color: "inherit",
                    }}
                  >
                    {timepoint}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-flow-col auto-cols-max">
          {filteredData.map((item, index) => {
            const header = Object.keys(item)[0];
            const details = item[header];

            // Check if there are any "VISIT" activities
            const hasVisit = details.some(
              (detail) => detail.activity_type === "VISIT",
            );

            // Sort details so that "VISIT" activities come first
            const sortedDetails = hasVisit
              ? details.sort((a, b) => {
                  if (a.activity_type === "VISIT") return -1;
                  if (b.activity_type === "VISIT") return 1;
                  return 0;
                })
              : details;
            return (
              <div key={index} className="flex flex-col mr-3">
                <div className="flex flex-col space-y-4">
                  {sortedDetails.map((activity, activityIndex) => (
                    <Card
                      key={activityIndex}
                      className="single-subject-card w-65 rounded-sm"
                      data-testid={`activity-card-${activityIndex}`}
                    >
                      <p
                        className="text-[#162429] font-semibold mb-1"
                        data-testid="card-title"
                      >
                        {activity.activity}
                      </p>
                      <div className="flex justify-between mb-1">
                        <div className="flex items-center ">
                          <CalendarIcon />
                          <span>{renderTags(activity.date)}</span>
                        </div>
                        <div className="flex items-center">
                          <AlarmIcon />
                          <span>{renderTags(activity.time)} </span>
                        </div>
                      </div>
                      {renderActivityStatusTag(activity.activity_status)}
                      <p className="text-[#2B4752] text-xs font-semibold">
                        Notes
                      </p>
                      <p className="text-[#556C75] text-xs italic text-wrap wrap-break-word">
                        {activity.notes}
                      </p>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
